import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Theme } from "@material-ui/core";
import moment from "moment";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import "moment/locale/fr";
import "moment/locale/it";
import "moment/locale/de";
import { ArrowBack, ArrowForward } from "@material-ui/icons";
import { Button } from "@material-ui/core";
import { WeekDaysNumberArray } from "../../data/constants";

interface CalendarProps {
  onChange?: (selected: Date[]) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  calendar: {
    alignSelf: "center",
    maxWidth: "400px",
    border: 0,
    "& tr,td,th": {
      border: 0,
    }
  },
  monthWrapper: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
  },
  selected: {
    cursor: "pointer",
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  pointer: {
    cursor: "pointer",
  },
}));

export const Calendar: React.FunctionComponent<CalendarProps> = ({ onChange }) => {
  const intl = useIntl();
  const classes = useStyles();
  const [currentDate, setCurrentDate] = React.useState<Date>(new Date());
  const [selectedDates, setSelectedDates] = React.useState<Date[]>([]);

  useEffect(() => {
    onChange?.(selectedDates);
  }
  , [onChange, selectedDates]);

  const handleNextOrPreviousMonth = (offset: number) => {
    setCurrentDate(moment(currentDate).add(offset, "months").toDate());
    setSelectedDates([]); // Reset selected dates when changing month
  };

  const getWeekRow = (date: Date) => {
    const startOfWeek = moment(date).isoWeekday() - 1;
    const nulls = Array.from({ length: startOfWeek }, () => null);
    const daysToEndOfMonth = moment(date).daysInMonth() - moment(date).date();
    const dates =
      daysToEndOfMonth >= 6 - startOfWeek
        ? Array.from({ length: 7 - startOfWeek }, (_, i) => moment(date).add(i, "days").toDate())
        : Array.from({ length: 7 - startOfWeek }, (_, i) => {
            const day = moment(date).add(i, "days");
            return day.month() === moment(date).month() ? day.toDate() : null;
          });
    return [...nulls, ...dates];
  };

  let currentDay = moment(currentDate).startOf("month");
  const rows = [];
  while (currentDay.month() === moment(currentDate).month()) {
    const weekRow = getWeekRow(currentDay.toDate());
    rows.push(weekRow);
    currentDay = weekRow[6] ? moment(weekRow[6]).add(1, "days") : moment(currentDay).add(1, "week");
  }

  const handleToggleDate = (date: Date) => {
    const newSelectedDates = selectedDates.find(d => moment(d).isSame(date, "day")) !== undefined
      ? selectedDates.filter((d) => !moment(d).isSame(date, "day"))
      : [...selectedDates, date];
    setSelectedDates(newSelectedDates);
    if (onChange) {
      onChange(newSelectedDates);
    }
  };

  const handleToggleWeekDay = (weekDay: number) => {
    let newSelectedDates = selectedDates.filter(
      (d) =>
        !moment(d)
          .isoWeekday(weekDay + 1)
          .isSame(d, "day")
    );
    const startOfMonth = moment(currentDate).startOf("month");
    const endOfMonth = moment(currentDate).endOf("month");

    for (let date = startOfMonth; date.isSameOrBefore(endOfMonth); date.add(1, "day")) {
      if (date.isoWeekday() === weekDay + 1 && !newSelectedDates.some((d) => moment(d).isSame(date, "day"))) {
        if (selectedDates.find(d => moment(d).isSame(date, "day")) !== undefined) {
          newSelectedDates = newSelectedDates.filter((d) => !moment(d).isSame(date, "day"));
        } else {
          newSelectedDates.push(date.toDate());
        }
      }
    }

    setSelectedDates(newSelectedDates);
    if (onChange) {
      onChange(newSelectedDates);
    }
  };

  const isDateSelected = (date: Date | null) => selectedDates.some((d) => moment(d).isSame(date, "day"));

  moment.locale(intl.locale.toLowerCase());

  return (
    <Table className={classes.calendar} size="small">
      <TableHead>
        <TableRow>
          <TableCell colSpan={7}>
            <div className={classes.monthWrapper}>
              <Button onClick={() => handleNextOrPreviousMonth(-1)}>
                <ArrowBack fontSize="small" />
              </Button>
              {moment(currentDate).format("MMM YYYY")}
              <Button onClick={() => handleNextOrPreviousMonth(1)}>
                <ArrowForward fontSize="small" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          {WeekDaysNumberArray.map((day) => (
            <TableCell key={day} onClick={() => handleToggleWeekDay(day)} className={classes.pointer}>
              {moment()
                .isoWeekday(day + 1)
                .format("ddd")}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((week, index) => (
          <TableRow key={index}>
            {week.map((date, i) => (
              <TableCell
                key={index * 7 + i}
                style={{ textAlign: "center", padding: "5px" }}
                className={isDateSelected(date) ? classes.selected : classes.pointer}
                onClick={() => date && handleToggleDate(date)}
              >
                {date ? moment(date).format("D") : ""}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default Calendar;
